import { motion, AnimatePresence } from 'motion/react';
import { Play, X, Video, Loader2 } from 'lucide-react';
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

type VideoType = {
  title: string;
  url: string;
  thumbnail: string;
};

type VideoSectionProps = {
  videos: VideoType[];
  title?: string;
};

const VideoModal = ({ isOpen, onClose, video }: { isOpen: boolean; onClose: () => void; video: VideoType | null }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!video) return null;

  // Convert Google Drive share URL to embed URL
  const getEmbedUrl = (url: string) => {
    const fileIdMatch = url.match(/\/d\/([a-zA-Z0-9-_]+)/);
    if (fileIdMatch) {
      return `https://drive.google.com/file/d/${fileIdMatch[1]}/preview`;
    }
    return url;
  };

  const modalContent = (
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex flex-col items-center justify-center overflow-hidden"
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
        >
          {/* Backdrop (clickable) */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/20 backdrop-blur-sm cursor-pointer"
            onClick={onClose}
          />

          {/* Modal Content container */}
          <motion.div
            initial={{ scale: 0.95, opacity: 0, y: 10 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 10 }}
            transition={{ type: "spring", damping: 30, stiffness: 400 }}
            className="relative w-full h-full flex flex-col items-center justify-center p-4 sm:p-8 lg:p-12 pointer-events-none"
          >
            {/* Top Bar - Close Button */}
            <div className="absolute top-4 right-4 sm:top-8 sm:right-8 z-50 pointer-events-auto">
              <button
                onClick={onClose}
                className="group p-2 sm:p-3 text-white transition-all bg-black/40 hover:bg-black/60 rounded-full backdrop-blur-xl border border-white/20 hover:scale-110 active:scale-95 shadow-2xl"
                aria-label="Close modal"
              >
                <X className="w-6 h-6 sm:w-8 sm:h-8" />
              </button>
            </div>

            {/* Video Container with Frame */}
            <div className="relative w-full max-w-5xl flex flex-col items-center justify-center pointer-events-none">
              <div className="relative w-full aspect-video bg-black rounded-3xl shadow-[0_20px_70px_rgba(0,0,0,0.5)] border-4 sm:border-[12px] border-white ring-1 ring-black/5 overflow-hidden flex items-center justify-center pointer-events-auto transition-all duration-500 scale-100 shadow-3xl">
                <iframe
                  src={getEmbedUrl(video.url)}
                  className="w-full h-full"
                  allow="autoplay; encrypted-media"
                  allowFullScreen
                  title={video.title}
                />
              </div>

              {/* Title / Footer */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="mt-6 sm:mt-8 pointer-events-auto"
              >
                <div className="inline-flex items-center gap-3 px-4 py-2 bg-black/40 backdrop-blur-md rounded-full border border-white/20 shadow-xl">
                  <span className="text-white/90 text-xs sm:text-sm font-bold tracking-wide">
                    {video.title}
                  </span>
                  <div className="w-1 h-1 bg-white/40 rounded-full" />
                  <span className="text-white/50 text-[10px] sm:text-xs">
                    Esc to close
                  </span>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return createPortal(modalContent, document.body);
};

const VideoThumbnail = ({ video, onClick }: { video: VideoType; onClick: () => void }) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const handleImageError = () => {
    setImageError(true);
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  return (
    <div className="group cursor-pointer" onClick={onClick}>
      <div className="relative aspect-video rounded-lg overflow-hidden bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 border border-white/20 hover:border-white/40 transition-all duration-300 group-hover:scale-[1.02]">
        {/* Thumbnail or Fallback */}
        {!imageError ? (
          <img
            src={video.thumbnail}
            alt={video.title}
            className={`w-full h-full object-cover transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
            onError={handleImageError}
            onLoad={handleImageLoad}
          />
        ) : null}

        {/* Fallback Background when image fails */}
        {imageError && (
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 flex items-center justify-center">
            <div className="text-center text-white">
              <Video className="w-12 h-12 mx-auto mb-2 opacity-60" />
              <p className="text-sm font-medium opacity-80">Video Demo</p>
            </div>
          </div>
        )}

        {/* Loading state */}
        {!imageLoaded && !imageError && (
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/50 via-purple-600/50 to-pink-600/50 flex items-center justify-center">
            <div className="text-center text-white">
              <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin mx-auto mb-2"></div>
              <p className="text-sm opacity-80">Loading...</p>
            </div>
          </div>
        )}

        {/* Overlay */}
        <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors duration-300" />

        {/* Play Button */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-16 h-16 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center border border-white/30 group-hover:bg-white/30 group-hover:scale-110 transition-all duration-300">
            <Play className="w-7 h-7 text-white ml-0.5" fill="currentColor" />
          </div>
        </div>

        {/* Play inline indicator */}
        <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="bg-white/20 backdrop-blur-md rounded px-3 py-2 text-sm text-white font-medium">
            Play inline
          </div>
        </div>

        {/* Video Title Overlay */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-4">
          <h5 className="text-white font-medium text-sm sm:text-base line-clamp-2">
            {video.title}
          </h5>
        </div>
      </div>
    </div>
  );
};

export const VideoSection = ({ videos, title = "Demo Videos" }: VideoSectionProps) => {
  const [selectedVideo, setSelectedVideo] = useState<VideoType | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (!videos || videos.length === 0) return null;

  const handleVideoClick = (video: VideoType) => {
    setSelectedVideo(video);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedVideo(null);
  };

  return (
    <div className="w-full">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-center mb-6"
      >
        <h4 className="text-lg sm:text-xl font-bold text-white mb-2">
          {title}
        </h4>
        <p className="text-white/80 text-sm sm:text-base">
          Watch our platform in action
        </p>
      </motion.div>

      <div className="flex flex-col gap-6">
        {videos.map((video, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1 }}
          >
            <VideoThumbnail
              video={video}
              onClick={() => handleVideoClick(video)}
            />
          </motion.div>
        ))}
      </div>

      {/* Video Modal */}
      <VideoModal isOpen={isModalOpen} onClose={closeModal} video={selectedVideo} />
    </div>
  );
};